import { useCallback, useEffect, useMemo, useState } from 'react';
import { friendApi, memberSummaryApi, userSearchApi } from '@formosa/shared/api/im';

/**
 * Multi-select: 搜尋好友（僅好友列表）／搜尋用戶（全站 username）
 * 搜尋只更新下方列表框，不觸發外層表單送出。
 */
export default function GroupMemberPicker({ value = [], onChange, excludeIds = [] }) {
  const [tab, setTab] = useState('friends');
  const [friends, setFriends] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [friendQ, setFriendQ] = useState('');
  const [keyword, setKeyword] = useState('');
  const [hits, setHits] = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [listError, setListError] = useState('');

  const excludeKey = (excludeIds || []).map(String).sort().join(',');
  const exclude = useMemo(
    () => new Set(excludeKey ? excludeKey.split(',') : []),
    [excludeKey]
  );
  const selectedIds = useMemo(
    () => new Set((value || []).map((v) => String(v.memberId))),
    [value]
  );

  const loadFriends = useCallback(() => {
    setFriendsLoading(true);
    setListError('');
    friendApi
      .listFriends()
      .then(async (data) => {
        const list = Array.isArray(data) ? data : [];
        setFriends(list);
        const ids = list.map((f) => f.memberId).filter((id) => !exclude.has(String(id)));
        if (!ids.length) {
          setSummaries({});
          return;
        }
        const rows = await memberSummaryApi.batch(ids);
        const map = {};
        (rows || []).forEach((s) => {
          if (s?.memberId != null) map[s.memberId] = s;
        });
        setSummaries(map);
      })
      .catch((e) => setListError(e?.message || '載入好友失敗'))
      .finally(() => setFriendsLoading(false));
  }, [exclude]);

  useEffect(() => {
    loadFriends();
  }, [loadFriends]);

  const toggle = (memberId, label) => {
    const key = String(memberId);
    if (exclude.has(key)) return;
    if (selectedIds.has(key)) {
      onChange((value || []).filter((v) => String(v.memberId) !== key));
      return;
    }
    onChange([...(value || []), { memberId, label: label || `會員 ${memberId}` }]);
  };

  const removeChip = (memberId) => {
    onChange((value || []).filter((v) => String(v.memberId) !== String(memberId)));
  };

  const runUserSearch = async () => {
    const q = keyword.trim();
    if (q.length < 2) {
      setListError('請輸入至少 2 字');
      setHits([]);
      setSearched(true);
      return;
    }
    setListError('');
    setSearchLoading(true);
    setSearched(true);
    try {
      const data = await userSearchApi.search({ keyword: q, limit: 20 });
      const list = Array.isArray(data) ? data : data?.data || [];
      setHits(list.filter((h) => !exclude.has(String(h.memberId)) && !h.isSelf));
    } catch (err) {
      setListError(err?.message || '搜尋失敗');
      setHits([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const filteredFriends = friends.filter((f) => {
    if (exclude.has(String(f.memberId))) return false;
    const s = summaries[f.memberId];
    const q = friendQ.trim().toLowerCase();
    if (!q) return true;
    const name = (s?.displayName || s?.username || '').toLowerCase();
    const un = (s?.username || '').toLowerCase();
    return name.includes(q) || un.includes(q) || String(f.memberId).includes(q);
  });

  return (
    <div className="group-picker">
      {value.length ? (
        <div className="group-picker__chips">
          {value.map((v) => (
            <button
              key={v.memberId}
              type="button"
              className="group-picker__chip"
              onClick={() => removeChip(v.memberId)}
              title="移除"
            >
              {v.label} ×
            </button>
          ))}
        </div>
      ) : (
        <p className="group-picker__hint">尚未選擇成員（可只建空群再加人）</p>
      )}

      <div className="chat-social-page__actions group-picker__tabs">
        <button
          type="button"
          className={tab === 'friends' ? 'is-active' : ''}
          onClick={() => {
            setTab('friends');
            setListError('');
          }}
        >
          搜尋好友
        </button>
        <button
          type="button"
          className={tab === 'search' ? 'is-active' : ''}
          onClick={() => {
            setTab('search');
            setListError('');
          }}
        >
          搜尋用戶
        </button>
      </div>

      {tab === 'friends' ? (
        <div className="chat-social-page__search">
          <input
            value={friendQ}
            onChange={(e) => setFriendQ(e.target.value)}
            placeholder="搜尋好友暱稱或 username…"
            autoComplete="off"
          />
        </div>
      ) : (
        <div className="chat-social-page__search">
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜尋全部用戶 username（至少 2 字）"
            autoComplete="off"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                runUserSearch();
              }
            }}
          />
          <button type="button" disabled={searchLoading} onClick={runUserSearch}>
            {searchLoading ? '…' : '搜尋'}
          </button>
        </div>
      )}

      <div className="group-picker__list-box" aria-live="polite">
        {listError ? <p className="chat-social-page__error group-picker__list-msg">{listError}</p> : null}

        {tab === 'friends' ? (
          <>
            {friendsLoading ? <p className="group-picker__list-msg">載入好友中…</p> : null}
            {!friendsLoading && filteredFriends.length === 0 ? (
              <p className="group-picker__list-msg">
                {friendQ.trim() ? '沒有符合的好友' : '沒有可選好友'}
              </p>
            ) : null}
            <ul className="chat-social-page__list group-picker__list">
              {filteredFriends.map((f) => {
                const s = summaries[f.memberId];
                const label = s?.displayName || s?.username || `會員 ${f.memberId}`;
                const checked = selectedIds.has(String(f.memberId));
                return (
                  <li key={f.memberId}>
                    <label className="group-picker__row">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(f.memberId, label)}
                      />
                      <span className="chat-social-page__name">{label}</span>
                      <span className="chat-social-page__meta">@{s?.username || f.memberId}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </>
        ) : (
          <>
            {searchLoading ? <p className="group-picker__list-msg">搜尋中…</p> : null}
            {!searchLoading && searched && hits.length === 0 && !listError ? (
              <p className="group-picker__list-msg">沒有符合的用戶</p>
            ) : null}
            {!searchLoading && !searched ? (
              <p className="group-picker__list-msg">輸入關鍵字後搜尋全部用戶</p>
            ) : null}
            <ul className="chat-social-page__list group-picker__list">
              {hits.map((h) => {
                const label = h.displayName || h.username || `會員 ${h.memberId}`;
                const checked = selectedIds.has(String(h.memberId));
                const blocked = h.blockedByMe || h.blockedMe;
                const canInvite = h.canInviteToGroup !== false && !blocked;
                return (
                  <li key={h.memberId}>
                    <label className={`group-picker__row ${canInvite ? '' : 'is-disabled'}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!canInvite && !checked}
                        onChange={() => toggle(h.memberId, label)}
                      />
                      <span className="chat-social-page__name">{label}</span>
                      <span className="chat-social-page__meta">
                        @{h.username}
                        {blocked ? ' · 已封鎖' : ''}
                        {h.canInviteToGroup === false ? ' · 不可邀群' : ''}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
